<script lang="ts">
  import Editor from "@distributor/svelte/Storefront/Containers/Editor.svelte";
  import FirstEditModal from "@distributor/svelte/Storefront/Components/FirstEditModal.svelte";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import Nav from "@distributor/svelte/Storefront/Containers/Nav.svelte";
  import SalesforceChat from "@shared/services/salesforce_chat";

  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  import { HttpPromise, HttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";

  import { debounce, get, omit } from "lodash";
  import { onMount } from "svelte";
  import { params } from "@roxi/routify";

  import type { Storefront } from "@shared/types/Storefront";

  let storefront: Storefront;
  let loading: boolean = true;
  let catalogPage: number = 1;

  let showFirstEditModal = !$CurrentUserStore.has_seen_first_storefront_edit_modal;

  const onCloseFirstEditModal = () => {
    HttpPromise({
      method: "PUT",
      url: "/api/distributor/user",
      data: {
        user: {
          has_seen_first_storefront_edit_modal: true,
        },
      },
    }).then(res => {
      CurrentUserStore.update(user => {
        user.has_seen_first_storefront_edit_modal = true;
        return user;
      });
    });
  };

  const fetchStorefront = async () => {
    try {
      storefront = await HttpPromise({
        method: "GET",
        url: "/api/distributor/storefronts/storefront_by_distributor_id",
        data: {
          distributor_id: $params.distributorId,
        },
      });
      loading = false;
      pullCatalog({});
    } catch (error) {
      loading = false;
    }
  };

  const pullProductLines = async (params, catalogPagination) => {
    const paramMap = {
      c: "category_id",
      s: "subcategory_id",
      t: "product_type_id",
      n: "container_type",
      m: "manufacturer_id",
      b: "brand_family_id",
      g: "tag_id",
      v: "vintage_year",
      r: "material",
    };

    Object.entries(params).forEach(([key, value]) => {
      if (paramMap[key]) {
        params[paramMap[key]] = value;
      }
    });

    const perPage = catalogPagination ? storefront.catalog_items_per_page : 50;

    const res = await HttpPromiseWithEntireResponse({
      method: "GET",
      url: "/api/distributor/product_lines/search",
      data: {
        ...params,
        require_inventory: true,
        limit: perPage,
        offset: perPage * (catalogPage - 1),
        distributor_id: storefront.distributor_id,
      },
    });

    return {
      headers: res.headers,
      data: await res.json(),
    };
  };

  const pullCatalog = async (params = {}) => {
    try {
      const res = await pullProductLines(params, true);
      const catalogPagination = {
        perPage: storefront.catalog_items_per_page,
        page: catalogPage,
        totalEntries: parseInt(res.headers.get("X-Pagination-Entries")),
        goToPage: n => {
          catalogPage = n;
          pullCatalog(params);
          return n;
        },
      };

      storefront = {
        ...storefront,
        catalog: res.data,
        catalogPagination,
      };
    } catch (error) {
      console.error(error);
    }
  };

  const pullAvailableFilters = async params => {
    params.specified_distributor_id = storefront.distributor_id;
    const res = await HttpPromise({
      method: "GET",
      url: "/api/distributor/product_lines/available_filters",
      data: params,
    });

    return {
      data: res,
    };
  };

  const refreshStorefront = async () => {
    loading = true;
    reloadIframe();

    try {
      const res = await HttpPromise({
        method: "GET",
        url: "/api/distributor/storefronts/storefront_by_distributor_id",
        data: {
          distributor_id: storefront.distributor_id,
        },
      });
      storefront = res;
      pullCatalog();
      loading = false;
    } catch (error) {
      console.error(error);
      loading = false;
    }
  };

  const updateInner = async (data, errorCB) => {
    loading = true;

    try {
      const res = await updateApiRequest(data);
      storefront = res;
      pullCatalog();
      reloadIframe();
      loading = false;
    } catch (error) {
      storefront = { ...storefront, errors: get(error, "errors.keys") };
      loading = false;
      if (errorCB) {
        errorCB(error.data);
      }
    }
  };
  const update = debounce(updateInner, 100);

  const updateApiRequest = data => {
    return HttpPromise({
      method: "PUT",
      url: "/api/distributor/storefronts/" + storefront.id,
      data: omit(data, "catalog"),
    });
  };

  const getPreviewIframe = () => {
    return document.getElementById("storefront-preview-content");
  };

  const reloadIframe = () => {
    const previewFrame = getPreviewIframe() as HTMLIFrameElement;
    if (!previewFrame || !previewFrame.contentWindow) {
      return;
    }
    previewFrame.contentWindow.postMessage("reload-storefront-preview", "*");
  };

  const scrollLookup = {
    header_and_logo: "end",
    featured_product_featured: "end",
    featured_items: "center",
    collections: "center",
    product_catalog: "start",
    footer: "start",
  };

  const scrollToElementInIframe = domId => {
    const previewFrame = getPreviewIframe() as HTMLIFrameElement;
    if (!previewFrame || !previewFrame.contentWindow) {
      return;
    }
    const scrollEl = previewFrame.contentWindow.document.getElementById(domId);
    if (scrollEl) {
      scrollEl.scrollIntoView({
        block: scrollLookup[domId] || "start",
        inline: "nearest",
        behavior: "smooth",
      });
      if (domId == "product_catalog") {
        setTimeout(() => {
          window.scrollBy(0, -100);
        }, 750);
      }
    }
  };

  const setLoading = loading => {
    loading = loading;
    storefront.loading = true;
  };

  const callbacks = {
    refreshStorefront,
    update,
    pullCatalog,
    pullProductLines,
    setLoading,
    pullAvailableFilters,
    scrollToElementInIframe,
  };

  onMount(async () => {
    fetchStorefront();
  });

  setTimeout(() => {
    SalesforceChat.hide();
  }, 1000);
</script>

<FizzStyles>
  <div class="fizz-layout-storefronts" style="margin-top:60px">
    {#if storefront}
      <div class="fizz-layout-sidebar-iframe storefronts-layout-sidebar-iframe">
        <div data-grid-area="header" />

        {#if showFirstEditModal}
          <FirstEditModal {showFirstEditModal} {onCloseFirstEditModal} />
        {/if}

        <Nav {callbacks} {storefront} {loading} />
        <Editor {storefront} {callbacks} />
      </div>
    {/if}
  </div>
</FizzStyles>
